/**
 * PATH: packages/quote-builder/src/api/client.ts
 * FILE_ID: QB-API-CLIENT-001
 * PURPOSE: Injectable HTTP client slot for the quote-builder package.
 * WHY: The builder's API layer was lifted out of the master SPA
 *      (convergence A3), where it used the master's axios instance
 *      (`src/reducers/apiClient` — bearer auth + refresh interceptors).
 *      A shared package must not import a shell's client, and silently
 *      switching transports (e.g. to cookie-auth quotechJson) was the
 *      exact risk that deferred client unification to slice C4. So the
 *      shell PRIMES this slot with its own configured client; the
 *      package only ever talks through it.
 * FLOW:
 *   ┌────────────────┐   ┌──────────────────────┐   ┌───────────────┐
 *   │ shell bootstrap│ → │ setQuoteBuilderApi-  │ → │ qbApi().get/… │
 *   │ (axios inst.)  │   │ Client(client)       │   │ in api modules│
 *   └────────────────┘   └──────────────────────┘   └───────────────┘
 * DEPENDENCIES: none (structural axios-like contract only)
 * RELATED:
 *   - src/features/quote-workbook/runtime.ts (master bridge, primes this)
 *   - C4 (one setupHubApi in @quotech/api) will collapse this slot later
 */

/**
 * Structural subset of an axios response — the fields the API layer reads.
 * `headers` stays optional so non-axios clients can satisfy the contract.
 */
/* eslint-disable @typescript-eslint/no-explicit-any --
 * `T = any` deliberately mirrors axios' AxiosResponse<any> default: the
 * lifted API layer narrows each endpoint's payload at the call site, and
 * defaulting to `unknown` would force ~60 redundant casts. */
export interface QuoteBuilderHttpResponse<T = any> {
  data: T
  status: number
  headers?: Record<string, unknown>
}

/**
 * Structural subset of an axios instance — everything the quote-builder
 * API layer actually calls. Any axios instance satisfies it.
 */
export interface QuoteBuilderHttpClient {
  get<T = any>(url: string, config?: unknown): Promise<QuoteBuilderHttpResponse<T>>
  post<T = any>(url: string, body?: unknown, config?: unknown): Promise<QuoteBuilderHttpResponse<T>>
  put<T = any>(url: string, body?: unknown, config?: unknown): Promise<QuoteBuilderHttpResponse<T>>
  patch<T = any>(url: string, body?: unknown, config?: unknown): Promise<QuoteBuilderHttpResponse<T>>
  delete<T = any>(url: string, config?: unknown): Promise<QuoteBuilderHttpResponse<T>>
}
/* eslint-enable @typescript-eslint/no-explicit-any */

let client: QuoteBuilderHttpClient | null = null

/** Prime the package with the shell's configured HTTP client. */
export function setQuoteBuilderApiClient(next: QuoteBuilderHttpClient): void {
  client = next
}

/** Test seam — reset the slot between specs. */
export function resetQuoteBuilderApiClient(): void {
  client = null
}

/**
 * Resolve the primed client. Throws loudly if the shell never primed it —
 * a silent no-op here would surface as "quotes never load" with no clue.
 */
export function qbApi(): QuoteBuilderHttpClient {
  if (!client) {
    throw new Error(
      '[quote-builder] API client not primed. The host shell must call ' +
        'setQuoteBuilderApiClient(<axios-like client>) before using quote-builder APIs.',
    )
  }
  return client
}
